import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DataType, IPoint, TypeUtility } from 'buzzyears.core';
import { VertexInteractionPoint, WorkflowEdge, WorkflowVertex } from 'component.workflow.core';
import { AbstractWorkflowGraphDispatcherObserver } from '../abstract-workflow-graph-dispatcher-observer';
import {
  WorkflowGraphDispatcherAction,
  WorkflowGraphDispatcherActionType as ActionType,
  WorkflowGraphDispatcherService
} from '../workflow-graph-dispatcher.service';
import { WorkflowGraphService } from '../workflow-graph.service';
import { WorkflowGraphVertexInteractionPoint } from '../vertex/interaction-point/workflow-graph-vertex-interaction-point';
import { WorkflowGraphSelectionService } from '../workflow-graph-selection.service';

@Component({
  selector: 'g[wf-graph-edge]',
  template: `
    <svg:path [attr.d]="path" stroke-linecap="round" marker-end="url(#ConnectionHead)" stroke-width="1"
              [attr.stroke-dasharray]="valid ? '0, 0' : '10, 10'"
              [attr.stroke]="strokeFill"/>

    <svg:g *ngIf="!edge.triggered && (toVertexSelected || fromVertexSelected)" [attr.transform]="nameTransform">
      <rect [attr.x]="nameX" [attr.y]="nameY" [attr.width]="nameWidth" [attr.height]="nameHeight"
            [attr.fill]="strokeFill"
            rx="5" ry="5"/>
      <svg:text x="0" [attr.y]="valueFontSize / 4 + 1" font-family="monospace" font-weight="bold"
                [attr.font-size]="valueFontSize + 'px'"
                text-anchor="middle" fill="#fff">
        {{ nameText }}
      </svg:text>
    </svg:g>

    <svg:g *ngIf="edge.triggered" [attr.transform]="valueTransform">
      <rect [attr.x]="valueX" [attr.y]="valueY" [attr.width]="valueWidth" [attr.height]="valueHeight" fill="#D3234E"
            rx="5" ry="5"/>
      <svg:text x="0" [attr.y]="valueFontSize / 4 + 1" font-family="monospace" font-weight="bold"
                [attr.font-size]="valueFontSize + 'px'"
                text-anchor="middle" fill="#fff">
        {{ valueToShow }}
      </svg:text>
    </svg:g>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowGraphEdgeComponent extends AbstractWorkflowGraphDispatcherObserver implements OnInit, OnDestroy {
  @Input() edge: WorkflowEdge;
  valueFontSize = 11;

  fromVertexSelected = false;
  toVertexSelected = false;

  constructor(dispatcherService: WorkflowGraphDispatcherService,
              protected cdr: ChangeDetectorRef,
              protected graphSelectionService: WorkflowGraphSelectionService,
              protected graphService: WorkflowGraphService) {
    super(dispatcherService);
  }


  onVertexSelected(vertex: WorkflowVertex) {
    const fromVertexSelected = vertex === this.edge.fromVertex;
    const toVertexSelected = vertex === this.edge.toVertex;

    let repaint = false;

    if (this.fromVertexSelected !== fromVertexSelected) {
      this.fromVertexSelected = fromVertexSelected;
      repaint = true;
    }

    if (this.toVertexSelected !== toVertexSelected) {
      this.toVertexSelected = toVertexSelected;
      repaint = true;
    }

    if (repaint) {
      this.repaint();
    }

  }

  repaint() {
    this.cdr.markForCheck();
  }

  get valueToShow(): string {
    const value = this.edge.value;

    if (TypeUtility.isArray(value)) {
      const key = `${this.edge.from.key}-items`,
        list = value as any[];
      return list.map(numObj => numObj[key]).join(', ');
    }

    return value !== undefined && value !== null ? (value instanceof Date ? value.toDateString() : value.toString()) : '';
  }

  get nameText(): string {
    if (this.fromVertexSelected) {
      return this.getPointName(this.edge.fromOutlet);
    }

    if (this.toVertexSelected) {
      return this.getPointName(this.edge.toInlet);
    }

    return '';
  }

  getPointName(point: VertexInteractionPoint) {

    if (!point) {
      return '';
    }

    return point.named ? point.name : '';
  }

  get hasValue(): boolean {
    return this.edge.value !== undefined;
  }

  get nameWidth(): number {
    const
      length = this.nameText.length,
      fontSize = this.valueFontSize * (length <= 5 ? 1 : 0.65);

    return fontSize * length;
  }

  get valueWidth(): number {
    const
      length = this.valueToShow.length,
      fontSize = this.valueFontSize * (length <= 5 ? 1 : 0.65);

    return fontSize * length;
  }

  get nameHeight(): number {
    return this.valueFontSize + 4;
  }

  get valueHeight(): number {
    return this.valueFontSize + 4;
  }

  get nameX(): number {
    const width = this.nameWidth,
      x = 0;

    return x - width / 2;
  }

  get valueX(): number {
    const width = this.valueWidth,
      x = 0;

    return x - width / 2;
  }

  get nameY(): number {
    const height = this.nameHeight,
      y = 0;

    return y - height / 2;
  }

  get valueY(): number {
    const height = this.valueHeight,
      y = 0;

    return y - height / 2;
  }

  get nameTransform(): string {
    const namePoint = this.namePoint;
    return `translate(${namePoint.x}, ${namePoint.y})`;
  }

  get valueTransform(): string {
    const valuePoint = this.valuePoint;
    return `translate(${valuePoint.x}, ${valuePoint.y})`;
  }

  get namePoint(): IPoint {
    const inlet = this.inlet,
      outlet = this.outlet,
      from = {
        x: this.edge.fromVertex.x + outlet.specification.at.x,
        y: this.edge.fromVertex.y + outlet.specification.at.y
      },
      to = {
        x: this.edge.toVertex.x + +inlet.specification.at.x,
        y: this.edge.toVertex.y + inlet.specification.at.y
      };

    let x = -100, y = -100;

    if (from && to) {
      x = (from.x + to.x) / 2;
      y = ((from.y + to.y) / 2);
    }

    return {
      x: x,
      y: y
    };
  }

  get valuePoint(): IPoint {
    const inlet = this.inlet,
      outlet = this.outlet,
      from = {
        x: this.edge.fromVertex.x + outlet.specification.at.x,
        y: this.edge.fromVertex.y + outlet.specification.at.y
      },
      to = {
        x: this.edge.toVertex.x + +inlet.specification.at.x,
        y: this.edge.toVertex.y + inlet.specification.at.y
      };

    let x = -100, y = -100;

    if (from && to) {
      x = (from.x + to.x) / 2;
      y = ((from.y + to.y) / 2);
    }

    return {
      x: x,
      y: y
    };
  }

  get valid(): boolean {
    const inlet = this.inlet,
      outlet = this.outlet;

    if (!inlet || !outlet) {
      return false;
    }

    const inPoint = inlet.point,
      outPoint = outlet.point;

    if (inPoint.dataType === DataType.Mixed || outPoint.dataType === DataType.Mixed) {
      return true;
    }

    return inPoint.dataType === outPoint.dataType;
  }

  get strokeFill(): string {
    return this.valid ? (this.fromVertexSelected ? '#ff7124' : (this.toVertexSelected ? '#0061FF' : '#000')) : '#F00';
  }

  get inlet(): WorkflowGraphVertexInteractionPoint {
    return this.graphService.getInlet(this.edge.toVertex, this.edge.inlet);
  }

  get outlet(): WorkflowGraphVertexInteractionPoint {
    return this.graphService.getOutlet(this.edge.fromVertex, this.edge.outlet);
  }

  get path(): string {

    const inlet = this.inlet,
      outlet = this.outlet;

    if (!inlet || !outlet) {
      return '';
    }

    const fromPoint = {
        x: this.edge.fromVertex.x + outlet.specification.at.x,
        y: this.edge.fromVertex.y + outlet.specification.at.y
      },
      toPoint = {
        x: this.edge.toVertex.x + +inlet.specification.at.x,
        y: this.edge.toVertex.y + inlet.specification.at.y
      };

    if (fromPoint && toPoint) {
      const x1 = fromPoint.x,
        y1 = fromPoint.y,

        x2 = toPoint.x,
        y2 = toPoint.y;

      const dx = x1 - x2,
        dy = -(y1 - y2),
        radius = 8;

      let inRads = Math.atan2(dy, dx);
      inRads = inRads < 0 ? Math.abs(inRads) : 2 * Math.PI - inRads;

      return [
        'M', x1 - (radius * Math.cos(inRads)), y1 - (radius * Math.sin(inRads)),
        'L', x2 + (radius * Math.cos(inRads)), y2 + (radius * Math.sin(inRads))
      ].join(' ');
    }

    return ' ';
  }

  onDispatcherEvent(action: WorkflowGraphDispatcherAction) {
    switch (action.type) {

      case ActionType.VertexSelected:
        this.onVertexSelected(action.data);
        break;

      case ActionType.WorkflowTested:
        this.repaint();
        break;

      case ActionType.VertexUpdated:
      case ActionType.VertexMoving:

        if (action.data === this.edge.fromVertex || action.data === this.edge.toVertex) {
          this.repaint();
        }

        break;
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }
}



// WEBPACK FOOTER //
// ./src/library/graph/edge/workflow-graph-edge.component.ts